















import {
    State,
    Action,
    Mutation,
    Getter
} from 'vuex-class'
import Noty from 'noty'

import { UI_SET_SIZE, UI_SET_TREE_OPEN, UI_SET_PAGE, INITIAL_CONNECT } from '@/store/ui/constants'
import { USER_LOAD } from '@/store/share/constants'

import { Component, Watch, Vue } from 'vue-property-decorator'
import Locker from '@/util/Locker'
import { Route } from 'vue-router'
import PStorage, { EMAIL_VERIFIED, stored } from '@/util/PStorage'
import Search from '@/util/Search'

import { RootState } from '@/store/types'
import { EditorEntry } from '@/store/editor/types'
import { UISize, EditorPage } from '@/store/ui/types'
import { BreakpointElement } from '@/directives/Breakpoints'
import Onboarding from '@/util/Onboarding'
import {
    FETCH_ENTRIES,
    VAULT_TREE_OPEN,
    VAULT_TREE_CLOSE_ALL,
    FETCH_UPDATES
} from '@/store/entries/constants'
import EntryRepository from '@/data/entry-repository'
import { Dictionary } from '@/types/general'
import { ENTRY_LOAD, EDITOR_CLEAR } from '@/store/editor/constants'
import { SETTINGS_LOAD_PREFERENCES } from '@/store/settings/constants'
import { RouteBuilder } from '@/router'
import Entry from '@/data/entry-model'
import { Update, checkForUpdates } from '@/util/update-check'
import { isElectron } from '@/util/Helpers'

import SidePanel from './sidepanel/SidePanel.vue'

const EntryView = () => import('./main/EntryView.vue')

/* eslint-disable indent */
@Component({
    name: 'main-app',
    components: {
        SidePanel,
        EntryView
    }
})
/* eslint-enable indent */
export default class MainApp extends Vue {
    @Action(FETCH_ENTRIES)
    fetchEntries!: () => void

    @Action(INITIAL_CONNECT)
    initialConnect!: () => Promise<void>

    @Action(ENTRY_LOAD)
    loadEntry!: (entryId: string) => void

    @Mutation(EDITOR_CLEAR)
    clearEditor!: () => void

    @Action(VAULT_TREE_OPEN)
    openVaultTree!: (id: string) => void

    @Action(VAULT_TREE_CLOSE_ALL)
    closeAllVaults!: () => void

    @Mutation(UI_SET_SIZE)
    setUISize!: (size: UISize) => void

    @Mutation(UI_SET_TREE_OPEN)
    setTreeOpen!: (value: boolean) => void

    @Mutation(UI_SET_PAGE)
    setPage!: (value: EditorPage) => void

    @State((state: RootState) => state.editor.blank)
    blank!: boolean

    @State((state: RootState) => state.editor.entry)
    entry!: EditorEntry

    @State((state: RootState) => state.editor.entry.existing)
    entryExisting!: boolean

    @State((state: RootState) => state.entries.allEntries)
    allEntries!: Dictionary<Entry>

    @State((state: RootState) => state.ui.size)
    uiSize!: string

    @State((state: RootState) => state.ui.treeOpen)
    treeOpen!: boolean

    @State((state: RootState) => state.ui.page)
    page!: string

    @State((state: RootState) => state.ui.isOnline)
    isOnline!: boolean

    @Getter
    canToggleTree!: boolean

    @Getter
    usingPages!: boolean

    @Watch('$route')
    onRouteChange(to: Route): void {
        this.doRoute(to)
    }

    @Watch('isOnline')
    async onIsOnlineChange(isOnline: boolean): Promise<void> {
        if (isOnline) {
            this.notifyEmailNotVerified()

            if (isElectron()) {
                const update = await checkForUpdates()
                if (update) {
                    this.notifyNewUpdate(update)
                }
            }
        }
    }

    async mounted(): Promise<void> {
        await this.fetchEntries()
        this.initialConnect()
        this.doRoute(this.$route)

        Locker.run(this)
        Locker.beginInterval(this);
        (window as any).Search = Search
    }

    async doRoute({ params }: Route): Promise<void> {
        // editor
        let newEntry = false
        if (params.id) {
            if (!this.entry || this.entry.id !== params.id) {
                this.setPage('editor')
                try {
                    await this.loadEntry(params.id)
                    if (this.treeOpen) {
                        // try close tree
                        this.setTreeOpen(false)
                    }
                    newEntry = true
                } catch (err) {
                    // entry doesn't exist - redirect
                    RouteBuilder.setEditRoute()
                }
            }
        } else {
            this.clearEditor()
        }
        if (params.trid) {
            if (Object.keys(this.allEntries).length > 0) {
                this.openVaultTree(params.trid)
                if (!newEntry && !this.treeOpen) {
                    this.setTreeOpen(true)
                }
            }
        } else {
            this.closeAllVaults()
        }
    }

    updateBreakpoints(applied: DOMTokenList): void {
        if (applied.contains('size-lg')) {
            if (this.uiSize !== 'lg-desktop') {
                this.setUISize('lg-desktop')
            }
        } else if (applied.contains('size-md')) {
            if (this.uiSize !== 'sm-desktop') {
                this.setUISize('sm-desktop')
            }
        } else if (applied.contains('size-sm')) {
            if (this.uiSize !== 'tablet') {
                this.setUISize('tablet')
            }
        } else if (this.uiSize !== 'mobile') {
            this.setUISize('mobile')
        }
    }

    notifyNewUpdate(update: Update): void {
        const noty = this.$notice({
            text: `<div>
                🎉 A new version of Volt is available!
                ${update.changelog}
            </div>`,
            timeout: false,
            buttons: [
                Noty.button(
                    'Download update',
                    'v-button v-button--primary-dull v-button--md',
                    () => {
                        require('electron').shell.openExternal(
                            'https://artifacts.volt.sh/?latest'
                        )
                        noty.close()
                    }
                )
            ],
            closeWith: ['button']
        })
    }

    /**
     * Notify the user if their email isn't verified
     */
    notifyEmailNotVerified(): void {
        if (stored.emailVerified === false) {
            const noty = this.$notice({
                text: `👋 Hey!<br/><br/>
                It looks like you haven't verified your email address yet. Verifying your email
                gives you access to the best features of Volt - like sharing, organisations and
                notifications. Please check your email and follow the link provided to verify.
                <br/><br/>
                Didn't receive an email? You can resend the verification using the button below.
                You can also check your email is correct in your account settings.
                `,
                timeout: false,
                buttons: [
                    Noty.button(
                        'Open account settings',
                        'v-button v-button--grey-dull v-button--md',
                        () => {
                            this.$router.push('preferences/account')
                            noty.close()
                        }
                    ),
                    Noty.button(
                        'Resend verification',
                        'v-button v-button--primary-dull v-button--md',
                        async () => {
                            const loading = this.$notice({ text: 'Sending email...' })
                            try {
                                await Onboarding.resendVerificationEmail()
                                this.$success({
                                    text: 'Verification email sent! Check your inbox.'
                                })
                                noty.close()
                            } catch (err) {
                                this.$error({ text: err })
                            } finally {
                                loading.close()
                            }
                        }
                    )
                ],
                closeWith: ['button']
            })
        }
    }
}
